import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { MessageCircle, Navigation } from 'lucide-react';
import { Button, Card, ErrorState, Money, Notice, PaymentStatusBadge, RideStatusBadge, Spinner } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { MapView } from '../../components/map/MapView';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime, fmtDuration, fmtKm } from '../../lib/format';
import type { RideDto } from '../../lib/types';
import { routeMarkers } from '../customer/RequestDetailPage';
import { CustomerCard, DriverVehicleCard } from './RideInfoCards';
import { BookingActions } from './BookingActions';
import { CustomerPaymentCard } from '../customer/CustomerPaymentCard';
import { RideIncidents } from '../safety/RideIncidents';
import { DestinationChangeDriver } from '../safety/DestinationChange';
import { ReferralDecision, ReferRide } from '../safety/Referral';
import { RideExtrasSection } from './RideExtrasSection';

export const useRide = (id: string) => useQuery({ queryKey: ['ride', id], queryFn: () => api<RideDto>(`/rides/${id}`) });

/** Detalle de un viaje (cliente o conductor). */
export function RideDetailPage({ role }: { role: 'customer' | 'driver' }) {
  const { id } = useParams<{ id: string }>();
  const q = useRide(id);
  const r = q.data;
  const markers = useMemo(() => (r ? routeMarkers(r) : []), [r]);
  const base = role === 'customer' ? '/app' : '/driver';

  return (
    <Page title={r ? r.code : 'Viaje'} back={role === 'customer' ? '/app/trips' : '/driver/calendar'} onRefresh={() => q.refetch()}>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {r && (
        <div className="flex flex-col gap-4">
          <MapView className="h-52 -mx-4 sm:mx-0 sm:rounded-3xl" markers={markers} route={r.route.polyline} interactive={false} />
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-bold text-lg">{fmtDateTime(r.scheduledAt)}</h2>
              <p className="text-sm text-[var(--text-muted)]">{r.service.name}{r.leg !== 'SINGLE' ? ` · ${r.leg === 'RETURN' ? 'Regreso' : 'Ida'}` : ''}</p>
            </div>
            <RideStatusBadge status={r.status} />
          </div>
          {r.status === 'CANCELLED' && (
            <Notice tone="danger">
              Viaje cancelado{r.cancelReason ? `: ${r.cancelReason}` : ''}.
              {r.cancellationFee ? <> Cargo por cancelación: <Money value={r.cancellationFee} className="font-semibold" />.</> : null}
            </Notice>
          )}
          {role === 'driver' && <DestinationChangeDriver ride={r} />}
          {role === 'customer' && r.referral && r.status === 'CONFIRMED' && (
            <Card className="p-4 border-2 border-brand-red/60 flex flex-col gap-3">
              <p className="font-semibold">Has sido referido a otro conductor de confianza</p>
              <ReferralDecision ride={r} />
            </Card>
          )}

          {role === 'driver' && ['CONFIRMED', 'DRIVER_ON_THE_WAY', 'DRIVER_ARRIVED', 'IN_PROGRESS'].includes(r.status) && (
            <Link to={`/driver/rides/${r.id}/live`}><Button block size="lg" variant="accent" icon={<Navigation className="size-5" />}>{r.status === 'CONFIRMED' ? 'Abrir servicio' : 'Volver al viaje activo'}</Button></Link>
          )}
          {role === 'customer' && ['DRIVER_ON_THE_WAY', 'DRIVER_ARRIVED', 'IN_PROGRESS'].includes(r.status) && (
            <Link to={`/app/rides/${r.id}/live`}><Button block size="lg" variant="accent" icon={<Navigation className="size-5" />}>Seguir en el mapa</Button></Link>
          )}
          {role === 'customer' ? <DriverVehicleCard ride={r} /> : <CustomerCard ride={r} />}

          <Card className="p-4 text-sm flex flex-col gap-2">
            <p><span className="text-[var(--text-muted)]">Recogida:</span> {r.origin.address}</p>
            {r.stops.map((s, i) => (
              <p key={i}><span className="text-[var(--text-muted)]">Parada {i + 1}:</span> {s.address}{i < r.stopsVisited ? ' ✓' : ''}</p>
            ))}
            <p><span className="text-[var(--text-muted)]">Destino:</span> {r.destination.address}</p>
            {r.route.distanceMeters != null && <p><span className="text-[var(--text-muted)]">Recorrido:</span> {fmtKm(r.route.distanceMeters)} · {fmtDuration(r.route.durationSeconds)}</p>}
            {r.notes && <p><span className="text-[var(--text-muted)]">Notas:</span> <span data-no-translate>{r.notes}</span></p>}
          </Card>

          {role === 'customer' && r.payment ? (
            <CustomerPaymentCard ride={r} />
          ) : (
            <Card className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--text-muted)]">{r.packageId ? 'Incluido en el paquete' : 'Tarifa acordada'}</p>
                {!r.packageId && <Money value={r.agreedFare} className="text-xl font-bold" />}
                {r.payment && Number(r.payment.balance) > 0 && <p className="text-xs text-[var(--text-muted)]">Saldo: <Money value={r.payment.balance} /></p>}
              </div>
              {r.payment && <PaymentStatusBadge status={r.payment.status} />}
            </Card>
          )}
          {role === 'driver' && r.payment && r.payment.status !== 'VOID' && ['IN_PROGRESS', 'COMPLETED'].includes(r.status) && (
            <Link to={`/driver/rides/${r.id}/payment`}><Button block variant={Number(r.payment.balance) > 0 ? 'accent' : 'secondary'}>{Number(r.payment.balance) > 0 ? 'Registrar cobro' : 'Ver cobro'}</Button></Link>
          )}

          {r.conversationId && (
            <Link to={`${base}/chat/${r.conversationId}`}><Button variant="secondary" block icon={<MessageCircle className="size-5" />}>Chat</Button></Link>
          )}
          {role === 'driver' && <ReferRide ride={r} />}
          <RideExtrasSection ride={r} role={role} />
          <BookingActions ride={r} role={role} />
          <RideIncidents ride={r} role={role} />
        </div>
      )}
    </Page>
  );
}
